import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Layout } from '../../components/layout/Layout';
import api from '../../api';
import { Input } from '../../components/ui/input';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { 
  Search, Camera, Filter, X,
  ChevronLeft, ChevronRight, Link2, Monitor, ChevronUp, ChevronDown, ChevronsUpDown
} from 'lucide-react';
import { toast } from 'sonner';
import { formatDateARG } from '../../lib/utils';

const VirtSnapshotsReport = () => {
  const [loading, setLoading] = useState(true);
  const [snapshots, setSnapshots] = useState([]);
  const [stats, setStats] = useState(null);
  const [pagination, setPagination] = useState({ page: 1, page_size: 25, total: 0, pages: 1 });
  const [filters, setFilters] = useState({ search: '', status: '' });
  
  // Sorting
  const [sortConfig, setSortConfig] = useState({ field: 'created_at', dir: 'asc' });
  
  // Active stat filter
  const [activeStatFilter, setActiveStatFilter] = useState('all');

  const fetchSnapshots = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: pagination.page.toString(),
        page_size: pagination.page_size.toString(),
        sort_by: sortConfig.field,
        sort_dir: sortConfig.dir,
      });
      if (filters.search) params.append('search', filters.search);
      if (filters.status) params.append('status', filters.status);

      const res = await api.get(`/reports/virt/snapshots?${params}`);
      setSnapshots(res.data.items);
      setPagination(prev => ({ ...prev, total: res.data.total, pages: res.data.pages }));
    } catch (err) {
      toast.error('Error al cargar snapshots');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const res = await api.get('/reports/virt/snapshots/stats');
      setStats(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  useEffect(() => {
    const t = setTimeout(fetchSnapshots, 300);
    return () => clearTimeout(t);
  }, [pagination.page, filters, sortConfig]);

  // Sort handler
  const handleSort = (field) => {
    setSortConfig(prev => ({
      field,
      dir: prev.field === field && prev.dir === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Sort icon component
  const SortIcon = ({ field }) => {
    if (sortConfig.field !== field) {
      return <ChevronsUpDown className="h-3 w-3 text-muted-foreground/50" />;
    }
    return sortConfig.dir === 'asc' 
      ? <ChevronUp className="h-3 w-3 text-primary" />
      : <ChevronDown className="h-3 w-3 text-primary" />;
  };

  // Stat card click handler
  const handleStatFilter = (filterType) => {
    setActiveStatFilter(filterType);
    setPagination(prev => ({ ...prev, page: 1 }));
    
    switch (filterType) {
      case 'critical':
        setFilters({ search: '', status: 'critical' });
        break;
      case 'warning':
        setFilters({ search: '', status: 'warning' });
        break;
      case 'ok':
        setFilters({ search: '', status: 'ok' });
        break;
      default:
        setFilters({ search: '', status: '' });
        break;
    }
  };

  const getStatusBadge = (status, days) => {
    switch (status) {
      case 'critical':
        return <Badge className="bg-red-500 text-white">{days} días</Badge>;
      case 'warning':
        return <Badge className="bg-yellow-500 text-black">{days} días</Badge>;
      case 'ok':
        return <Badge className="bg-green-500 text-white">{days} días</Badge>;
      default:
        return <Badge variant="outline">{days} días</Badge>;
    }
  };

  const clearFilters = () => {
    setFilters({ search: '', status: '' });
    setActiveStatFilter('all');
  };

  const hasFilters = filters.search || filters.status;

  const formatSize = (gb) => {
    if (!gb) return '0 GB';
    return `${Number(gb).toFixed(1)} GB`;
  };

  // Sortable header component
  const SortableHeader = ({ field, children, className = '' }) => (
    <th 
      className={`p-4 cursor-pointer hover:bg-muted/70 select-none ${className}`}
      onClick={() => handleSort(field)}
    >
      <div className="flex items-center gap-1">
        {children}
        <SortIcon field={field} />
      </div>
    </th>
  );

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-semibold">Reporte de Snapshots</h1>
          <p className="text-sm text-muted-foreground">Monitoreo de snapshots por antigüedad (≥7 días warning, ≥30 días crítico)</p>
        </div>

        {/* Stats - Clickeable Cards (thresholds: 7d warning, 30d critical) */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <Card 
              className={`card-subtle cursor-pointer transition-all hover:border-primary/50 ${activeStatFilter === 'all' ? 'border-primary ring-1 ring-primary/20' : ''}`}
              onClick={() => handleStatFilter('all')}
              data-testid="stat-total"
            >
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold">{stats.total || 0}</p>
                <p className="text-xs text-muted-foreground">Total</p>
              </CardContent>
            </Card>
            <Card 
              className={`card-subtle cursor-pointer transition-all hover:border-red-500/50 ${activeStatFilter === 'critical' ? 'border-red-500 ring-1 ring-red-500/20' : ''}`}
              onClick={() => handleStatFilter('critical')}
              data-testid="stat-critical"
            >
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-red-500">{stats.critical || 0}</p>
                <p className="text-xs text-muted-foreground">≥ 30 días (Crítico)</p>
              </CardContent>
            </Card>
            <Card 
              className={`card-subtle cursor-pointer transition-all hover:border-yellow-500/50 ${activeStatFilter === 'warning' ? 'border-yellow-500 ring-1 ring-yellow-500/20' : ''}`}
              onClick={() => handleStatFilter('warning')}
              data-testid="stat-warning"
            >
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-yellow-500">{stats.warning || 0}</p>
                <p className="text-xs text-muted-foreground">7-30 días (Warning)</p>
              </CardContent>
            </Card>
            <Card 
              className={`card-subtle cursor-pointer transition-all hover:border-green-500/50 ${activeStatFilter === 'ok' ? 'border-green-500 ring-1 ring-green-500/20' : ''}`}
              onClick={() => handleStatFilter('ok')}
              data-testid="stat-ok"
            >
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-green-500">{stats.ok || 0}</p>
                <p className="text-xs text-muted-foreground">&lt; 7 días (OK)</p>
              </CardContent>
            </Card>
            <Card className="card-subtle">
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold">{formatSize(stats.total_size_gb)}</p>
                <p className="text-xs text-muted-foreground">Tamaño Total</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filters */}
        <Card className="card-subtle">
          <CardContent className="p-4">
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2">
                <Filter className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm font-medium">Filtros:</span>
              </div>
              <div className="flex items-center gap-2 flex-1 min-w-[200px]">
                <Search className="h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Buscar VM o snapshot..."
                  value={filters.search}
                  onChange={(e) => {
                    setFilters(prev => ({ ...prev, search: e.target.value }));
                    setActiveStatFilter('');
                  }}
                  className="h-9"
                  data-testid="snapshot-search-input"
                />
              </div>
              <Select 
                value={filters.status || "todos"} 
                onValueChange={(v) => {
                  setFilters(prev => ({ ...prev, status: v === "todos" ? "" : v }));
                  setActiveStatFilter('');
                }}
              >
                <SelectTrigger className="w-44 h-9" data-testid="snapshot-status-filter">
                  <SelectValue placeholder="Estado" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos</SelectItem>
                  <SelectItem value="critical">Crítico (≥30 días)</SelectItem>
                  <SelectItem value="warning">Warning (7-30 días)</SelectItem>
                  <SelectItem value="ok">OK (&lt;7 días)</SelectItem>
                </SelectContent>
              </Select>
              {hasFilters && (
                <Button variant="ghost" size="sm" onClick={clearFilters}>
                  <X className="h-4 w-4 mr-1" /> Limpiar
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Table */}
        <Card className="card-subtle">
          <CardContent className="p-0">
            <table className="w-full text-sm" data-testid="snapshots-table">
              <thead className="bg-muted/50">
                <tr className="border-b">
                  <SortableHeader field="snapshot_name" className="text-left">SNAPSHOT</SortableHeader>
                  <SortableHeader field="vm_name" className="text-left">VM</SortableHeader>
                  <SortableHeader field="server_hostname" className="text-left">SERVIDOR</SortableHeader>
                  <SortableHeader field="created_at" className="text-left">FECHA</SortableHeader>
                  <SortableHeader field="age_days" className="text-center justify-center">ANTIGÜEDAD</SortableHeader>
                  <SortableHeader field="size_gb" className="text-right justify-end">TAMAÑO</SortableHeader>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={6} className="text-center p-8 text-muted-foreground">Cargando...</td></tr>
                ) : snapshots.length === 0 ? (
                  <tr><td colSpan={6} className="text-center p-8 text-muted-foreground">No hay snapshots</td></tr>
                ) : snapshots.map(snap => (
                  <tr key={snap.id} className="border-b hover:bg-muted/30" data-testid={`snapshot-row-${snap.id}`}>
                    <td className="p-4">
                      <div className="flex items-center gap-2">
                        <Camera className="h-4 w-4 text-muted-foreground" />
                        <div>
                          <span className="font-medium">{snap.snapshot_name}</span>
                          {snap.is_current && (
                            <Badge variant="outline" className="ml-2 text-xs">Actual</Badge>
                          )}
                        </div>
                      </div>
                      {snap.description && (
                        <p className="text-xs text-muted-foreground ml-6">{snap.description}</p>
                      )}
                    </td>
                    <td className="p-4">
                      <div className="flex items-center gap-2">
                        <Monitor className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm">{snap.vm_name}</span>
                      </div>
                    </td>
                    <td className="p-4">
                      {snap.server_id ? (
                        <Link 
                          to={`/assets/servers/${snap.server_hostname}`} 
                          className="flex items-center gap-1 text-blue-500 hover:underline text-sm"
                        >
                          <Link2 className="h-3 w-3" />
                          {snap.server_hostname}
                        </Link>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </td>
                    <td className="p-4 text-sm text-muted-foreground">
                      {formatDateARG(snap.created_at)}
                    </td>
                    <td className="p-4 text-center">
                      {getStatusBadge(snap.alert_status, snap.age_days)}
                    </td>
                    <td className="p-4 text-right text-sm">
                      {snap.size_gb?.toFixed(2)} GB
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>

        {/* Pagination */}
        {pagination.pages > 1 && (
          <div className="flex justify-center items-center gap-4">
            <Button 
              variant="outline" 
              size="sm" 
              disabled={pagination.page === 1}
              onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-sm text-muted-foreground">
              Página {pagination.page} de {pagination.pages} ({pagination.total} snapshots)
            </span>
            <Button 
              variant="outline" 
              size="sm" 
              disabled={pagination.page === pagination.pages}
              onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default VirtSnapshotsReport;
